<template>
  <section class="about-page">
    <!-- HERO -->
    <header class="hero" role="banner">
      <div class="container hero-inner">
        <h1 class="hero-title">{{ hero.title }}</h1>
        <p class="hero-sub">{{ hero.subtitle }}</p>
      </div>
    </header>

    <!-- INTRO VIDEO SECTION -->
    <AboutIntro />

    <!-- FACTORY & GALLERY -->
    <section class="factory-section" data-aos="fade-up">
      <div class="container factory-grid">
        <div class="factory-text">
          <h2 class="section-title">{{ factory.title }}</h2>
          <p class="lead">{{ factory.lead }}</p>

          <ul class="strength-list">
            <li v-for="(item, i) in factory.strengths" :key="i">
              {{ item }}
            </li>
          </ul>
        </div>

        <div class="factory-gallery">
          <Gallery :images="factoryGallery" />
        </div>
      </div>
    </section>

    <!-- TIMELINE -->
    <section class="timeline-section" aria-label="Company timeline">
      <div class="container">
        <h2 class="section-title" data-aos="fade-up">Our Journey</h2>

        <div class="timeline" data-aos="fade-up" data-aos-delay="80">
          <div class="timeline-line" aria-hidden="true"></div>

          <div
            v-for="(step, idx) in history"
            :key="idx"
            class="timeline-item"
            :class="{
              left: !isMobile && idx % 2 === 0,
              right: !isMobile && idx % 2 === 1,
            }"
            :data-aos="'fade-up'"
            :data-aos-delay="120 + idx * 40"
          >
            <div class="marker-wrapper" aria-hidden="true">
              <div class="marker-glow"></div>
              <div class="marker-dot"></div>
              <div class="marker-year">{{ step.year }}</div>
            </div>

            <div class="item-card">
              <h3 class="item-title">{{ step.title }}</h3>
              <p class="item-text">{{ step.text }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- YOLEE MISSION -->
    <section class="mission-section" data-aos="fade-up">
      <div class="container">
        <h2 class="section-title">YOLEE — Role & Direction</h2>

        <div class="mission-intro">
          <p>
            <strong>YOLEE</strong> serves as the
            <strong>Exclusive Sales Representative (ESR)</strong> of SHIXIAO
            Machinery. Established as an independently operated LLC backed by
            SHIXIAO’s ownership, YOLEE is the factory-authorized organization
            for international sales, technical communication and customer
            support.
          </p>

          <p>
            Our team spends time inside factory operations and conducts regular
            overseas visits — ensuring technical accuracy, fast response and
            procurement transparency.
          </p>
        </div>

        <div class="mission-grid">
          <div class="mission-card">
            <h3>Our Mission</h3>
            <p>
              Bridge global manufacturers with engineering-led production
              partners, delivering clear technical communication and dependable
              supply.
            </p>
          </div>

          <div class="mission-card">
            <h3>Our Vision</h3>
            <p>
              Build long-term partnerships through quality, process knowledge
              and responsible growth — not price-driven competition.
            </p>
          </div>

          <div class="mission-card">
            <h3>Customer Promise</h3>
            <p>
              Fast technical response, accurate specifications and long-term
              spare part availability to support repeatable production.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="cta-section" data-aos="fade-up">
      <div class="container cta-inner">
        <div class="cta-copy">
          <h2 class="cta-title">Work With Us</h2>
          <p class="cta-sub">
            Interested in factory visits, OEM projects or sample testing? YOLEE
            handles international sales, technical support and logistics
            coordination.
          </p>
        </div>

        <div class="cta-actions">
          <router-link
            class="btn primary"
            to="/contact"
            role="button"
            aria-label="Contact Sales"
          >
            Contact Sales
          </router-link>

          <router-link
            class="btn outline"
            :to="{ path: '/categories/main/1' }"
            role="button"
            aria-label="View Products"
          >
            View Products
          </router-link>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import Gallery from "@/components/Gallery";
import AboutIntro from "./components/AboutIntro.vue";
import AOS from "aos";
import { aboutAssets, aboutContent } from "@/config/about";

export default {
  name: "AboutPage",
  components: { Gallery, AboutIntro },

  data() {
    return {
      windowWidth: window.innerWidth,
    };
  },

  computed: {
    isMobile() {
      return this.windowWidth <= 768;
    },

    hero() {
      return aboutContent.hero;
    },

    factory() {
      return aboutContent.factory;
    },

    history() {
      return aboutContent.history;
    },

    factoryGallery() {
      return aboutAssets.factoryGallery;
    },
  },

  mounted() {
    // SEO meta
    try {
      document.title =
        "About YOLEE – Exclusive Sales Representative of SHIXIAO Machinery";
      const setMeta = (name, content) => {
        let el = document.querySelector(`meta[name="${name}"]`);
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute("name", name);
          document.head.appendChild(el);
        }
        el.setAttribute("content", content);
      };
      setMeta(
        "description",
        "YOLEE is the Exclusive Sales Representative (ESR) of SHIXIAO Machinery. We provide factory-authorized technical sales, global logistics and long-term support for abrasive machinery and consumables.",
      );
      setMeta(
        "keywords",
        "SHIXIAO, YOLEE, abrasive machinery, flap discs, backing plates, exclusive sales representative",
      );
    } catch (e) {
      // ignore
    }

    // AOS already initialized globally in main.js — refresh here for correct positions
    if (AOS && typeof AOS.refresh === "function") {
      AOS.refresh();
    }

    // window resize listener to handle device-mode switching in devtools
    window.addEventListener("resize", this.onResize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },

  methods: {
    onResize() {
      this.windowWidth = window.innerWidth;
      // ensure AOS recalculates positions after layout changes
      if (AOS && typeof AOS.refreshHard === "function") AOS.refreshHard();
    },
  },
};
</script>

<style lang="less" scoped>
@import "~@/assets/styles/variables.less";

/* Container */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}

/* HERO */
.hero {
  padding: 2rem 0;
  padding-bottom: 0;
  background: linear-gradient(
    180deg,
    fade(@color-bg-light, 8%),
    @color-bg-main
  );

  .hero-inner {
    text-align: center;
    .hero-title {
      font-size: 34px;
      color: @color-text-title;
      font-weight: 700;
      margin-bottom: 8px;

      @media (max-width: 768px) {
        font-size: 24px;
      }
    }
    .hero-sub {
      color: @color-text-sub;
      max-width: 840px;
      margin: 0 auto;
      font-size: 16px;

      @media (max-width: 768px) {
        font-size: 14px;
        padding: 0 6px;
      }
    }
  }
}

/* SECTION TITLE */
.section-title {
  font-size: 22px;
  font-weight: 700;
  color: @color-text-title;
  margin-bottom: 18px;

  @media (max-width: 768px) {
    font-size: 18px;
  }
}

/* TIMELINE */
.timeline-section {
  padding: 36px 0;
}
.timeline {
  position: relative;
  padding-top: 8px;
  padding-bottom: 16px;
}

/* central line (visual only) */
.timeline-line {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 6px;
  top: 0;
  bottom: 0;
  background: linear-gradient(
    180deg,
    fade(@color-primary, 20%),
    fade(@color-active, 30%)
  );
  border-radius: 6px;

  @media (max-width: 768px) {
    left: 28px;
    width: 4px;
  }
}

.timeline-item {
  position: relative;
  width: 50%;
  box-sizing: border-box;
  padding: 18px 22px;
  margin: 36px 0;

  @media (max-width: 768px) {
    width: 100%;
    padding-left: 72px;
    margin: 18px 0;
  }
}

.timeline-item.left {
  left: 0;
  text-align: right;
  padding-right: 90px;
}

.timeline-item.right {
  left: 50%;
  padding-left: 90px;
}

/* Marker */
.marker-wrapper {
  position: absolute;
  top: 18px;
  width: 120px;
  pointer-events: none;

  @media (max-width: 768px) {
    left: 8px !important;
    width: 48px;
    top: 20px;
  }
}

.timeline-item.left .marker-wrapper {
  right: -110px;
  text-align: center;
}
.timeline-item.right .marker-wrapper {
  left: -110px;
  text-align: center;
}

.marker-glow {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: fade(@color-primary, 20%);
  filter: blur(12px);
  margin: 0 auto;
}
.marker-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: @color-primary;
  margin: -28px auto 6px;
  box-shadow: 0 6px 18px fade(@color-primary, 30%);
}
.marker-year {
  font-size: 13px;
  color: @color-text-sub;
  font-weight: 700;
}

/* Item Card */
.item-card {
  background: #fff;
  border-radius: 12px;
  padding: 18px;
  box-shadow: 0 12px 28px rgba(16, 24, 32, 0.06);

  .item-title {
    font-size: 18px;
    margin: 0 0 8px;
    color: @color-text-title;
    font-weight: 700;
  }
  .item-text {
    color: @color-text-sub;
    line-height: 1.6;
    margin: 0 0 12px;
    font-size: 15px;
  }
}

/* timeline gallery spacing */
.timeline-gallery {
  margin-top: 10px;
}

/* FACTORY SECTION */
.factory-section {
  padding: 28px 0;
}
.factory-grid {
  display: grid;
  grid-template-columns: 1fr 480px;
  gap: 28px;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
}
/* ⭐ 核心修复点 */
.factory-text,
.factory-gallery {
  min-width: 0;
}

/* 保险（可选，但推荐） */
.factory-gallery {
  max-width: 100%;
  overflow: hidden;
}

.factory-text .lead {
  margin-top: 6px;
  color: @color-text-sub;
  font-size: 15px;
}
.strength-list {
  margin-top: 12px;
  padding-left: 18px;
  color: @color-text-sub;
}
.strength-list li {
  margin-bottom: 10px;
  line-height: 1.6;
}

/* MISSION */
.mission-section {
  padding: 28px 0;
}
.mission-intro p {
  color: @color-text-sub;
  line-height: 1.7;
}
.mission-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 14px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
}
.mission-card {
  background: @color-bg-gray;
  padding: 16px;
  border-radius: 10px;
  h3 {
    margin: 0 0 8px;
    color: @color-text-title;
  }
  p {
    margin: 0;
    color: @color-text-sub;
    line-height: 1.6;
  }
}

/* CTA */
.cta-section {
  padding: 28px 0;
  background: linear-gradient(
    180deg,
    fade(@color-bg-light, 6%),
    @color-bg-main
  );
}
.cta-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
}

.cta-copy {
  flex: 1;
}
.cta-title {
  margin: 0 0 8px;
  font-size: 20px;
  color: @color-text-title;
}
.cta-sub {
  margin: 0;
  color: @color-text-sub;
  line-height: 1.6;
}

/* CTA Buttons */
.cta-actions {
  display: flex;
  gap: 12px;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    margin-top: 12px;
  }
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 22px;
  border-radius: 8px;
  font-weight: 600;
  border: 2px solid transparent;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.22s ease;
  line-height: 1;
}

.btn.primary {
  background: @color-primary;
  color: #fff;
  box-shadow: 0 8px 20px fade(@color-primary, 20%);
}
.btn.primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 30px fade(@color-primary, 25%);
}
.btn.outline {
  background: #fff;
  color: @color-primary;
  border-color: @color-primary;
}
.btn.outline:hover {
  background: fade(@color-primary, 10%);
  transform: translateY(-3px);
}

/* small devices: make buttons full width */
@media (max-width: 768px) {
  .btn {
    width: 100%;
    display: inline-flex;
    justify-content: center;
  }
}

/* accessibility focus */
.btn:focus {
  outline: 3px solid fade(@color-primary, 18%);
  outline-offset: 2px;
}

/* subtle helpers */
.lead {
  font-size: 15px;
}

/* keep [data-aos] smoother */
[data-aos] {
  will-change: transform, opacity;
}
</style>
